/**
 * Due to the Global nature of stylesheets, and to avoid conflicts, Please move all first-party global CSS imports to your application.
 *
 * import 'react-datepicker/dist/react-datepicker.css';
 */

import { Box, BoxProps, Flex, FormLabel } from '@chakra-ui/react';
import { CalendarIcon } from '@sphere/icons';
import { rem } from 'polished';
import React, { useCallback, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useUITranslation } from '../../context/TranslationProvider';
import DatePickerFooter from './DatePickerFooter';
import TimePicker from './TimePicker';
import { datePickerStyle } from './datePickerStyle';

type Props = Omit<BoxProps, 'onChange'> & {
  labelText: string;
  onChange(date?: Date): void;
  defaultDate?: Date;
  minDate?: Date;
  locale?: string;
  placeholder?: string;
  isValidDate?: (date: Date) => boolean;
  id?: string;
};

export const DatePickerBox = ({
  labelText,
  onChange,
  defaultDate,
  minDate,
  isValidDate,
  locale,
  placeholder,
  id = 'datePicker',
  ...boxProps
}: Props) => {
  const { t } = useUITranslation();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(defaultDate);
  const datePickerInputRef = useRef<ReactDatePicker<never, undefined> | null>(null);

  const setFocusToInput = useCallback(() => {
    if (datePickerInputRef.current) {
      datePickerInputRef.current.setFocus();
    }
  }, [datePickerInputRef]);

  const handleChange = (date: Date | null) => {
    onChange(date ?? undefined);
    setSelectedDate(date ?? undefined);
  };

  const handleClose = useCallback(
    (e: React.SyntheticEvent, shouldClear = false) => {
      e.stopPropagation();

      if (datePickerInputRef?.current) {
        datePickerInputRef.current.setOpen(false);
        if (shouldClear) {
          setSelectedDate(undefined);
        }
      }
    },
    [datePickerInputRef],
  );

  return (
    <Box
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.2"
      borderStyle="solid"
      py="space.16"
      px="space.24"
      w="full"
      transitionProperty="common"
      transitionDuration="normal"
      _hover={{
        borderColor: 'gray.5',
      }}
      _focusWithin={{
        borderColor: 'gray.5',
      }}
      {...boxProps}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Box>
          <FormLabel
            mb="space.4"
            textTransform="uppercase"
            fontSize="xs"
            fontWeight="bold"
            color="gray.4"
            htmlFor={id}
          >
            {labelText}
          </FormLabel>

          <Flex alignItems="center" onClick={setFocusToInput}>
            <Box>{placeholder}</Box>
            <Box flex="1" pr="space.24" sx={datePickerStyle}>
              <ReactDatePicker
                id={id}
                selected={selectedDate}
                onChange={handleChange}
                showTimeInput
                // @ts-ignore
                customTimeInput={<TimePicker isValidDate={isValidDate} />}
                fixedHeight
                minDate={minDate}
                timeIntervals={30}
                timeInputLabel="End time"
                locale={locale}
                dateFormat="MM/dd/yyyy h:mm aa"
                ref={datePickerInputRef}
                placeholderText={placeholder || t('components.date-picker-box.placeholder')}
                popperModifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [5, 10],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      rootBoundary: 'viewport',
                      tether: false,
                      altAxis: true,
                    },
                  },
                ]}
                showPopperArrow={false}
              >
                <DatePickerFooter handleClose={handleClose} />
              </ReactDatePicker>
            </Box>
          </Flex>
        </Box>
        <CalendarIcon cursor="pointer" boxSize={rem(32)} onClick={setFocusToInput} />
      </Flex>
    </Box>
  );
};
